import { ChevronDown } from "lucide-react";
import { useEffect } from "react";

import { useFeedHistory } from "../api/hooks.js";
import { FeedStatePill, feedStateCopy } from "../design-system/feed-state.js";
import { formatDeliveryFormat, formatRelative } from "../lib/format.js";
import { useNow } from "../lib/use-now.js";
import { Artwork, LiveBars } from "./artwork.js";
import { BlockedPanel } from "./blocked-notice.js";
import { usePlayer } from "./player-provider.js";
import { AiredProgress, PlayButton } from "./transport.js";

/**
 * The full-screen now-playing view, on mobile only. It is the moment the product
 * stops being a list and becomes the thing it is for, so it gets the whole screen,
 * the artwork at the size the artwork deserves, and the accent it was themed with.
 */
export function NowPlayingSheet({ onClose }: { onClose: () => void }) {
  const { detail, live, state, stop, target, toggle } = usePlayer();
  const airing = live.data?.nowPlaying ?? null;
  const now = useNow(1000);
  const history = useFeedHistory(
    target?.feedId ?? "",
    airing?.historyId ?? null,
    target !== null,
  );
  const delivery = live.data?.deliveries[0];

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    const { overflow } = document.body.style;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = overflow;
    };
  }, [onClose]);

  if (!target) return null;

  const recent = (history.data ?? []).filter(
    (item) => item.historyId !== airing?.historyId,
  );

  return (
    <div
      aria-label={`Now playing on ${target.feedName}`}
      aria-modal="true"
      className="fixed inset-0 z-50 flex animate-[radio-rise_240ms_cubic-bezier(0.22,1,0.36,1)] flex-col overflow-y-auto bg-bg bg-[radial-gradient(120%_60%_at_50%_0%,var(--accent-soft),transparent_70%)] lg:hidden"
      role="dialog"
    >
      <div className="flex items-center justify-between px-4 pt-[max(0.75rem,env(safe-area-inset-top))] pb-2">
        <button
          aria-label="Close the now playing view"
          className="grid size-10 place-items-center rounded-full text-ink-2 transition-colors hover:bg-surface"
          onClick={onClose}
          type="button"
        >
          <ChevronDown aria-hidden="true" className="size-5" />
        </button>
        <div className="flex min-w-0 flex-col items-center">
          <span className="font-mono text-[0.625rem] tracking-[0.14em] text-ink-3 uppercase">
            Listening to
          </span>
          <span className="max-w-[60vw] truncate text-sm font-semibold text-ink">
            {target.feedName}
          </span>
        </div>
        <div className="size-10" />
      </div>

      <div className="flex flex-1 flex-col gap-6 px-6 pt-4 pb-[max(2rem,env(safe-area-inset-bottom))]">
        <Artwork
          airing={airing}
          className="mx-auto w-full max-w-[19rem] shadow-[0_24px_60px_var(--accent-glow)]"
        />

        <div className="text-center">
          <h1 className="text-balance text-xl leading-tight font-semibold text-ink">
            {airing?.title ?? target.feedName}
          </h1>
          {airing?.artist ? (
            <p className="mt-1.5 text-sm text-ink-2">{airing.artist}</p>
          ) : null}
          <div className="mt-3 flex items-center justify-center gap-2.5">
            <FeedStatePill state={live.data?.state ?? null} />
            {state === "playing" ? (
              <LiveBars className="text-accent-text" />
            ) : null}
          </div>
          {live.data?.state && live.data.state !== "healthy" ? (
            <p className="mt-2 text-xs text-ink-3">
              {feedStateCopy(live.data.state).detail}
            </p>
          ) : null}
        </div>

        <AiredProgress now={now} snapshot={live.data} withTimes />

        {state === "blocked" ? <BlockedPanel streamUrl={detail} /> : null}
        {state === "error" && detail ? (
          <p className="text-center text-sm text-err">{detail}</p>
        ) : null}

        <div className="flex justify-center">
          <PlayButton
            feedName={target.feedName}
            onActivate={() => {
              if (
                state === "idle" ||
                state === "error" ||
                state === "blocked"
              ) {
                toggle(target);
              } else stop();
            }}
            size="large"
            state={state}
          />
        </div>

        {delivery ? (
          <p className="text-center font-mono text-[0.6875rem] text-ink-3">
            {formatDeliveryFormat(delivery)}
          </p>
        ) : null}

        {recent.length > 0 ? (
          <section className="mt-2">
            <h2 className="font-mono text-[0.625rem] tracking-[0.14em] text-ink-3 uppercase">
              Recently played
            </h2>
            <ul className="mt-3 divide-y divide-border/70">
              {recent.slice(0, 6).map((item) => (
                <li
                  key={item.historyId}
                  className="flex items-center gap-3 py-2.5"
                >
                  <Artwork airing={item} className="size-9" />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm text-ink">
                      {item.title}
                    </div>
                    {item.artist ? (
                      <div className="truncate text-xs text-ink-3">
                        {item.artist}
                      </div>
                    ) : null}
                  </div>
                  <span className="shrink-0 font-mono text-[0.6875rem] text-ink-3">
                    {formatRelative(item.startedAt, now)}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        ) : null}
      </div>
    </div>
  );
}
